import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * Global SAFE MODE banner. Renders across every authenticated page when the
 * backend reports POSTPEER_PUBLISH_ENABLED=false. Hard-blocks silently on
 * any fetch error so the app stays usable even if the endpoint is down.
 */
export default function SafeModeBanner() {
  const { getAuthHeader, user } = useAuth();
  const [state, setState] = useState({ loading: true, safe: false, text: '', hasLive: false, envDebug: null, fetchError: false });
  const isAdmin = user?.email === 'ashley@xchannels.ai';

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/integrations/postpeer/safe-mode`, {
          headers: getAuthHeader(),
        });
        if (cancelled) return;
        const hasLive = Array.isArray(data?.live_allowlist) && data.live_allowlist.length > 0;
        setState({
          loading: false,
          safe: !!data?.safe_mode,
          text: data?.banner_text || 'SAFE MODE — publishing disabled, no posts go live.',
          hasLive,
          envDebug: data?.env_debug || null,
          fetchError: false,
        });
      } catch (e) {
        if (cancelled) return;
        // Fail-closed: keep whatever state we last successfully loaded so a
        // transient network hiccup or auth flap does NOT flash a bogus
        // SAFE MODE banner in production. We only surface a subtle
        // connection-warning flag (admin-visible only) so we can still
        // diagnose real outages without disrupting end users.
        setState((prev) => ({
          ...prev,
          loading: false,
          fetchError: true,
        }));
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [user, getAuthHeader]);

  if (state.loading || !state.safe) return null;

  // Amber for pure SAFE MODE, rose for "SAFE MODE with live channels" so
  // operators can never miss that at least one channel goes live.
  const barColor = state.hasLive
    ? 'bg-rose-600 text-white'
    : 'bg-amber-500/95 text-black';

  return (
    <div
      data-testid="safe-mode-banner"
      className={`w-full ${barColor} text-xs font-semibold px-4 py-2 flex flex-col items-center justify-center gap-1 shadow-md z-50`}
      role="status"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span data-testid="safe-mode-banner-text" className="text-sm">{state.text}</span>
      </div>
      {isAdmin && state.envDebug && (
        <div
          data-testid="safe-mode-banner-env-debug"
          className="text-[10px] font-mono opacity-80"
        >
          Backend liest: POSTPEER_PUBLISH_ENABLED={state.envDebug.POSTPEER_PUBLISH_ENABLED}
          {state.envDebug.POSTPEER_GO_LIVE_AFTER && state.envDebug.POSTPEER_GO_LIVE_AFTER !== '(unset)'
            ? ` · CUTOFF=${state.envDebug.POSTPEER_GO_LIVE_AFTER}`
            : ''}
          {state.envDebug.backend_hostname && state.envDebug.backend_hostname !== '?'
            ? ` · host=${state.envDebug.backend_hostname}`
            : ''}
        </div>
      )}
      {isAdmin && state.fetchError && (
        <div
          data-testid="safe-mode-banner-fetch-error"
          className="text-[10px] font-mono opacity-80"
        >
          (Warnung: safe-mode Endpunkt aktuell nicht erreichbar — letzter bekannter Status wird angezeigt)
        </div>
      )}
    </div>
  );
}
